/*
 * Page de desinscription des emails de rappel.
 * Le jeton unique arrive par le lien de l'email : aucune autre donnee
 * n'est transmise. La desinscription est immediate et idempotente.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, CircleAlert, MailX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { unsubscribeRelance } from "@/lib/api";

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("jeton") || searchParams.get("token") || "").trim();
  const [status, setStatus] = useState(token ? "loading" : "invalid"); // loading | done | error | invalid

  const run = useCallback(async () => {
    if (!token) return;
    setStatus("loading");
    try {
      await unsubscribeRelance(token);
      setStatus("done");
    } catch (e) {
      setStatus(e?.response?.status === 404 ? "invalid" : "error");
    }
  }, [token]);

  useEffect(() => {
    window.scrollTo(0, 0);
    run();
  }, [run]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-[560px] w-full mx-auto px-4 sm:px-6 py-14">
        <div className="flex items-center gap-2 text-[#235277]">
          <MailX className="w-5 h-5" aria-hidden="true" />
          <span className="text-xs font-semibold tracking-[0.15em] uppercase">Rappels par email</span>
        </div>
        <h1 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-[#060606]">
          Désinscription des rappels
        </h1>

        <div className="mt-8 rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)]">
          {status === "loading" && (
            <div className="space-y-3" data-testid="unsubscribe-loading">
              <Skeleton className="h-5 w-3/4 rounded" />
              <Skeleton className="h-5 w-1/2 rounded" />
            </div>
          )}

          {status === "done" && (
            <div className="flex items-start gap-3" data-testid="unsubscribe-success">
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-[#114E0B]" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-[#060606]">C'est fait.</p>
                <p className="mt-1 text-sm leading-relaxed text-[#333333]">
                  Tu ne recevras plus d'email de rappel au sujet de ton bilan CYCLIQ.
                  Cette désinscription est immédiate. Ton bilan reste entre tes mains
                  et tu peux réserver un échange avec Gino quand tu le souhaites.
                </p>
              </div>
            </div>
          )}

          {status === "invalid" && (
            <div className="flex items-start gap-3" data-testid="unsubscribe-invalid">
              <CircleAlert className="w-5 h-5 shrink-0 mt-0.5 text-[#5E5515]" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-[#060606]">Lien invalide ou expiré.</p>
                <p className="mt-1 text-sm leading-relaxed text-[#333333]">
                  Ce lien de désinscription n'est pas reconnu. Si tu reçois encore des
                  rappels, utilise le lien présent dans le dernier email reçu ou écris
                  à contact@ginobodyandsoul.com.
                </p>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-3" data-testid="unsubscribe-error">
              <div className="flex items-start gap-3">
                <CircleAlert className="w-5 h-5 shrink-0 mt-0.5 text-[#D74843]" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-[#333333]">
                  La désinscription n'a pas pu être enregistrée pour le moment. Réessaie
                  dans un instant.
                </p>
              </div>
              <Button
                onClick={run}
                data-testid="unsubscribe-retry-button"
                className="min-h-[48px] rounded-xl bg-[#235277] hover:bg-[#1E4767] font-heading font-semibold transition-colors duration-200"
              >
                Réessayer
              </Button>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-[#9E9E9E]">
          <Link to="/" data-testid="unsubscribe-home-link" className="underline underline-offset-2 text-[#235277]">
            Retour à l'accueil
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
